import { Button } from '@vfit/shared/atoms';
import { API, purifyTextWithLogo, trackLink } from '@vfit/shared/data-access';
import {
  CONSUMER_CMS_SHOPPING_CART,
  CONSUMER_CMS_DEFAULT,
  useCmsConfig,
  ICoverageToolCongifg,
  IPersonalInfoCMS,
  goToHubProduct,
  IGenericErrorCMS,
  handleFWA,
  handleKo,
  handleR2Logged,
} from '@vfit/consumer/data-access';
import { purify } from '@vfit/shared/themes';
import { CoverageErrorType } from '@vfit/shared/models';
import { ICoverageErrorProps } from './coverageToolError.models';
import {
  ButtonSlideContainer,
  Container,
  Title,
  Description,
  TextContainer,
} from './coverageToolError.style';
import { organizeRedirect } from './coverageToolError.utils';
import CoverageToolErrorTagging from './coverageToolError.tagging';
import { useQueryClient } from 'react-query';

const CoverageToolError = ({
  onEdit,
  onClose,
  onRedirectClick,
  formattedAddress,
  coverageErrorType,
  product,
  isButtonSticky,
  isAlternativeFWA,
}: ICoverageErrorProps) => {
  const client = useQueryClient();
  const coverageToolConfig = useCmsConfig(
    CONSUMER_CMS_DEFAULT,
    API.CMS_GET_COVERAGE_TOOL_CONFIG
  ) as ICoverageToolCongifg;
  const commonConfig = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_PERSONAL_DATA
  ) as IPersonalInfoCMS;
  const genericError = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GENERIC_ERROR
  ) as IGenericErrorCMS;
  const {
    titleFwa,
    descriptionFwa,
    buttonListFwa,
    linkRedirectFwa,
    titleR2,
    descriptionR2,
    buttonListR2,
  } = organizeRedirect({ product, commonConfig, isAlternativeFWA });

  const getCoverageToolMessage = () => {
    switch (coverageErrorType) {
      case CoverageErrorType.KO:
      case CoverageErrorType.NO_COVERAGE:
        return {
          title: coverageToolConfig?.coveragetool?.koCoverageTool?.title || '',
          description:
            coverageToolConfig?.coveragetool?.koCoverageTool?.description.replace(
              'PLACEHOLDER',
              `${formattedAddress}` || ''
            ) || '',
          buttonList: [
            {
              text: coverageToolConfig?.coveragetool?.koCoverageTool?.buttonEditAddress || '',
            },
            {
              text: coverageToolConfig?.coveragetool?.koCoverageTool?.buttonCallsYou || '',
            },
          ],
        };
      case CoverageErrorType.NO_COVERAGE_AMD_FW:
        return {
          title:
            coverageToolConfig?.coveragetool?.noCoverageVffw?.title ||
            coverageToolConfig?.coveragetool?.koCoverageTool?.title ||
            '',
          description:
            coverageToolConfig?.coveragetool?.noCoverageVffw?.description?.replace(
              'PLACEHOLDER',
              `${formattedAddress}` || ''
            ) ||
            coverageToolConfig?.coveragetool?.koCoverageTool?.description.replace(
              'PLACEHOLDER',
              `${formattedAddress}` || ''
            ) ||
            '',
          buttonList: [
            {
              text:
                coverageToolConfig?.coveragetool?.noCoverageVffw?.buttonEditAddress ||
                coverageToolConfig?.coveragetool?.koCoverageTool?.buttonEditAddress ||
                '',
            },
            {
              text:
                coverageToolConfig?.coveragetool?.noCoverageVffw?.buttonCallsYou ||
                coverageToolConfig?.coveragetool?.koCoverageTool?.buttonCallsYou ||
                '',
            },
          ],
        };
      case CoverageErrorType.FWA:
        return {
          title: titleFwa,
          description: descriptionFwa,
          buttonList: buttonListFwa,
        };
      case CoverageErrorType.ERROR:
        return {
          title: genericError?.genericerror?.title || 'Ops!',
          description:
            genericError?.genericerror?.description ||
            'Si è verificato un errore. Riprovare in seguito',
          buttonList: [
            {
              text: '',
            },
            {
              text: genericError.genericerror.buttonLabel || 'Chiudi',
            },
          ],
        };
      case CoverageErrorType.R2_LOGGED:
        return {
          title: titleR2,
          description: descriptionR2,
          buttonList: buttonListR2,
        };
      default:
        return {
          title: coverageToolConfig?.coveragetool?.koCoverageTool?.title || '',
          description:
            coverageToolConfig?.coveragetool?.koCoverageTool?.description.replace(
              'PLACEHOLDER',
              `${formattedAddress}` || ''
            ) || '',
          buttonList: [
            {
              text: coverageToolConfig?.coveragetool?.koCoverageTool?.buttonEditAddress || '',
            },
            {
              text: coverageToolConfig?.coveragetool?.koCoverageTool?.buttonCallsYou || '',
            },
          ],
        };
    }
  };

  const { title, description, buttonList } = getCoverageToolMessage();
  const [pTitle, pDescription] = purify([title, description], purifyTextWithLogo);

  const handleClickEditCoverageToolError = () => {
    if (onEdit) onEdit();
  };

  const handleClickActionCoverageToolError = () => {
    switch (coverageErrorType) {
      case CoverageErrorType.NO_COVERAGE_AMD_FW:
      case CoverageErrorType.KO:
        handleKo(
          client,
          product?.urlClickToCallNoCoverage || product?.callMeNowHelp?.url || '',
          product
        );
        break;
      case CoverageErrorType.FWA:
        trackLink(product?.redirectFWA?.title || '');
        handleFWA(client, linkRedirectFwa, product);
        break;
      case CoverageErrorType.R2_LOGGED:
        handleR2Logged(client, product?.callMeNowHelp?.url || '', product);
        break;
      case CoverageErrorType.ERROR:
        goToHubProduct();
        break;
      default:
        if (onClose) onClose();
        break;
    }
  };

  const handleRedirectCoverageToolError = () => {
    trackLink(product?.redirectFWA?.alternativeOfferRedirect?.labelRedirect || '');
    if (onRedirectClick) onRedirectClick();
  };

  return (
    <>
      <CoverageToolErrorTagging coverageErrorType={coverageErrorType} />
      <Container>
        <TextContainer isSticky={isButtonSticky}>
          {pTitle && <Title dangerouslySetInnerHTML={{ __html: pTitle }} />}
          {pDescription && <Description dangerouslySetInnerHTML={{ __html: pDescription }} />}
        </TextContainer>
        <ButtonSlideContainer isSticky={isButtonSticky}>
          {buttonList?.[0]?.text && (
            <Button
              onClick={
                isAlternativeFWA
                  ? handleClickActionCoverageToolError
                  : handleClickEditCoverageToolError
              }
              variant={'primary'}
              name="action_ko"
            >
              {buttonList[0].text}
            </Button>
          )}
          {buttonList?.[1]?.text && (
            <Button
              onClick={
                isAlternativeFWA
                  ? handleRedirectCoverageToolError
                  : handleClickActionCoverageToolError
              }
              variant={'secondary'}
              name="action_ko_second"
            >
              {buttonList[1].text}
            </Button>
          )}
        </ButtonSlideContainer>
      </Container>
    </>
  );
};
export default CoverageToolError;

